import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import api from '../services/api';
import Toast from '../components/Toast';
import ExerciseInfoModal from '../components/ExerciseInfoModal';
import { CATEGORY_LABELS } from '../constants';
import './AdminTemplates.css';

export default function AdminTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  // Each entry: { exercise_id, name, category, gif_url, sets, reps, rest_seconds, order_index }
  const [formExercises, setFormExercises] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showExPicker, setShowExPicker] = useState(false);
  const [templateExSearch, setTemplateExSearch] = useState('');
  const [infoExercise, setInfoExercise] = useState(null);

  // Paginated picker state
  const [exPickerExercises, setExPickerExercises] = useState([]);
  const [exPickerLoading, setExPickerLoading] = useState(false);
  const [exPickerLoadingMore, setExPickerLoadingMore] = useState(false);

  const exPickerSentinelRef    = useRef(null);
  const exPickerSearchTimeout  = useRef(null);
  const exPickerParamsRef      = useRef({ search: '' });
  const exPickerPageRef        = useRef(1);
  const exPickerHasMoreRef     = useRef(false);
  const exPickerLoadingMoreRef = useRef(false);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      if (err.response?.status === 403) navigate('/today', { replace: true });
      else showToast('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const fetchExPicker = useCallback(async ({ search: s, page: p }, append = false) => {
    if (!append) setExPickerLoading(true);
    else { setExPickerLoadingMore(true); exPickerLoadingMoreRef.current = true; }
    try {
      const res = await api.get('/exercises', {
        params: { ...(s && { search: s }), global_only: 1, sort: 'alpha', page: p, per_page: 100 },
      });
      const d = res.data;
      if (append) setExPickerExercises(prev => [...prev, ...(d.exercises || [])]);
      else setExPickerExercises(d.exercises || []);
      exPickerHasMoreRef.current = d.has_more || false;
      exPickerPageRef.current = p;
      exPickerParamsRef.current = { search: s };
    } catch {}
    finally {
      setExPickerLoading(false);
      setExPickerLoadingMore(false);
      exPickerLoadingMoreRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!showExPicker) return;
    const sentinel = exPickerSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && exPickerHasMoreRef.current && !exPickerLoadingMoreRef.current) {
        fetchExPicker({ search: exPickerParamsRef.current.search, page: exPickerPageRef.current + 1 }, true);
      }
    }, { rootMargin: '200px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showExPicker, exPickerLoading, fetchExPicker]);

  function openExPicker() {
    setTemplateExSearch('');
    setExPickerExercises([]);
    fetchExPicker({ search: '', page: 1 });
    setShowExPicker(true);
  }

  function handleExPickerSearch(val) {
    setTemplateExSearch(val);
    clearTimeout(exPickerSearchTimeout.current);
    exPickerSearchTimeout.current = setTimeout(() => {
      fetchExPicker({ search: val, page: 1 });
    }, 300);
  }

  function openNew() {
    setEditingTemplate(null);
    setFormName('');
    setFormDesc('');
    setFormExercises([]);
    setShowEditor(true);
  }

  async function openEdit(t) {
    setEditingTemplate(t);
    setFormName(t.name);
    setFormDesc(t.description || '');
    setFormExercises([]);
    setShowEditor(true);
    try {
      const res = await api.get(`/admin/templates/${t.id}`);
      const detail = res.data.template;
      setFormExercises((detail.exercises || []).map(ex => ({
        exercise_id: ex.exercise_id,
        name: ex.exercise?.name || `#${ex.exercise_id}`,
        category: ex.exercise?.category || 'other',
        gif_url: ex.exercise?.gif_url || null,
        sets: ex.sets,
        reps: ex.reps,
        rest_seconds: ex.rest_seconds,
        order_index: ex.order_index,
      })));
    } catch {
      showToast('Error al cargar los ejercicios de la plantilla');
    }
  }

  function addExercise(ex) {
    setFormExercises(prev => [
      ...prev,
      {
        exercise_id: ex.id,
        name: ex.name,
        category: ex.category || 'other',
        gif_url: ex.gif_url || null,
        sets: ex.category === 'cardio' ? 1 : 3,
        reps: ex.category === 'cardio' ? '20' : '10',
        rest_seconds: ex.category === 'cardio' ? 0 : 60,
        order_index: prev.length,
      },
    ]);
    setShowExPicker(false);
  }

  function updateExField(idx, field, value) {
    setFormExercises(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  }

  function removeExercise(idx) {
    setFormExercises(prev => prev.filter((_, i) => i !== idx).map((e, i) => ({ ...e, order_index: i })));
  }

  async function handleSave(e) {
    e.preventDefault();
    const name = formName.trim();
    if (!name) { showToast('El nombre de la plantilla es obligatorio'); return; }
    if (formExercises.length === 0) { showToast('Añade al menos un ejercicio a la plantilla'); return; }
    setSaving(true);
    try {
      const payload = {
        name,
        description: formDesc.trim() || null,
        exercises: formExercises.map(({ exercise_id, sets, reps, rest_seconds, order_index }) => ({
          exercise_id, sets, reps, rest_seconds, order_index,
        })),
      };
      if (editingTemplate) {
        await api.put(`/admin/templates/${editingTemplate.id}`, payload);
        showToast('¡Plantilla actualizada!', 'success');
      } else {
        await api.post('/admin/templates', payload);
        showToast('¡Plantilla creada!', 'success');
      }
      setShowEditor(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al guardar la plantilla');
    } finally {
      setSaving(false);
    }
  }

  async function handleExpand(t) {
    const next = expanded === t.id ? null : t.id;
    setExpanded(next);
    if (next && !expandedDetails[t.id]) {
      try {
        const res = await api.get(`/admin/templates/${t.id}`);
        setExpandedDetails(prev => ({ ...prev, [t.id]: res.data.template }));
      } catch {
        showToast('Error al cargar ejercicios de la plantilla');
      }
    }
  }

  async function handleDelete(tId) {
    if (confirmDeleteId !== tId) { setConfirmDeleteId(tId); return; }
    try {
      await api.delete(`/admin/templates/${tId}`);
      showToast('Plantilla eliminada', 'success');
      setConfirmDeleteId(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al eliminar');
    }
  }

  const alreadyAddedIds = new Set(formExercises.map(fe => fe.exercise_id));
  const availablePicker = exPickerExercises.filter(ex => !alreadyAddedIds.has(ex.id));

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}

      <div className="page">
        <div className="page-header">
          <div className="page-title">Plantillas</div>
          <span className="text-muted text-sm">{templates.length}</span>
        </div>

        <div className="admin-tabs px-16">
          <button type="button" className="admin-tab" onClick={() => navigate('/admin')}>Usuarios</button>
          <button type="button" className="admin-tab" onClick={() => navigate('/admin/stats')}>Estadísticas</button>
          <button type="button" className="admin-tab active" onClick={() => {}}>Plantillas</button>
          <button type="button" className="admin-tab" onClick={() => navigate('/admin/options')}>Opciones</button>
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : (
          <div className="px-16">
            {templates.length === 0 && !showEditor ? (
              <div className="empty-state" style={{ padding: '40px 0' }}>
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">Sin plantillas</div>
                <div className="empty-state-text">Crea tu primera plantilla de rutina para asignar a usuarios.</div>
              </div>
            ) : (
              <div className="template-list">
                {templates.map(t => (
                  <div key={t.id} className="template-card card mb-8">
                    <button
                      className="session-card-header"
                      onClick={() => handleExpand(t)}
                      type="button"
                      style={{ width: '100%' }}
                    >
                      <div>
                        <div className="fw-600">{t.name}</div>
                        <div className="text-muted text-xs">
                          {t.exercise_count} ejercicios
                          {t.description ? ` · ${t.description}` : ''}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.7rem', color: '#a0a0b0' }}>
                        {expanded === t.id ? '▲' : '▼'}
                      </span>
                    </button>
                    {expanded === t.id && (
                      <div className="session-detail" style={{ paddingTop: 8 }}>
                        <div className="divider" />
                        {!expandedDetails[t.id] ? (
                          <div className="loading-container" style={{ padding: '12px 0' }}><div className="spinner spinner-sm" /></div>
                        ) : (
                          (expandedDetails[t.id].exercises || []).map((ex, i) => (
                            <div key={i} className="template-ex-item" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <span className="text-sm" style={{ color: '#eaeaea', fontWeight: 500 }}>
                                  {i + 1}. {ex.exercise?.name || `Ejercicio #${ex.exercise_id}`}
                                </span>
                                <span className="text-xs text-muted" style={{ marginLeft: 8 }}>
                                  {ex.sets}×{ex.reps} · {ex.rest_seconds}s
                                </span>
                              </div>
                              {ex.exercise?.gif_url && (
                                <button
                                  type="button"
                                  onClick={() => setInfoExercise(ex.exercise)}
                                  style={{ flexShrink: 0, background: 'none', border: '1px solid #2a2a4a', borderRadius: 6, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0a0b0', cursor: 'pointer', fontSize: '0.75rem' }}
                                  aria-label="Ver info"
                                >
                                  i
                                </button>
                              )}
                            </div>
                          ))
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-xs btn-primary" onClick={() => openEdit(t)}>
                            ✏️ Editar
                          </button>
                          {confirmDeleteId === t.id ? (
                            <>
                              <button type="button" className="btn btn-xs btn-secondary" onClick={() => setConfirmDeleteId(null)}>Cancelar</button>
                              <button type="button" className="btn btn-xs btn-danger" onClick={() => handleDelete(t.id)}>Confirmar</button>
                            </>
                          ) : (
                            <button type="button" className="btn btn-xs btn-danger" onClick={() => handleDelete(t.id)}>
                              🗑 Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button className="btn btn-primary btn-full mt-16" onClick={openNew} type="button">
              + Nueva plantilla
            </button>
          </div>
        )}
      </div>

      {showEditor && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowEditor(false)}>
          <div className="modal-sheet">
            <div className="modal-handle" />
            <div className="modal-title">{editingTemplate ? 'Editar plantilla' : 'Nueva plantilla'}</div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={formName} onChange={e => setFormName(e.target.value)} placeholder="ej. Push Pull Legs" required />
              </div>
              <div className="form-group">
                <label className="form-label">Descripción</label>
                <input className="form-input" value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="Opcional" />
              </div>

              {formExercises.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <label className="form-label" style={{ marginBottom: 6 }}>Ejercicios ({formExercises.length})</label>
                  {formExercises.map((fe, idx) => {
                    const isCardio = fe.category === 'cardio';
                    return (
                      <div key={idx} className="template-ex-editor">
                        <div className="flex items-center" style={{ gap: 6, marginBottom: 4 }}>
                          <span className={`badge badge-${(fe.category || 'other').toLowerCase()}`} style={{ fontSize: '0.6rem', padding: '1px 5px' }}>
                            {CATEGORY_LABELS[(fe.category || 'other').toLowerCase()] || 'Otro'}
                          </span>
                          <span className="text-sm fw-600" style={{ flex: 1 }}>{fe.name || `#${fe.exercise_id}`}</span>
                          {fe.gif_url && (
                            <button
                              type="button"
                              onClick={() => setInfoExercise({ name: fe.name, category: fe.category, gif_url: fe.gif_url })}
                              style={{ flexShrink: 0, background: 'none', border: '1px solid #2a2a4a', borderRadius: 6, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0a0b0', cursor: 'pointer', fontSize: '0.75rem' }}
                              aria-label="Ver info"
                            >
                              i
                            </button>
                          )}
                          <button type="button" onClick={() => removeExercise(idx)} className="set-remove-btn" style={{ width: 24, height: 24 }}>×</button>
                        </div>
                        {isCardio ? (
                          <div className="form-group" style={{ marginTop: 4, marginBottom: 0 }}>
                            <input className="form-input form-input-sm" type="text" value={fe.reps} onChange={e => updateExField(idx, 'reps', e.target.value)} placeholder="Tiempo (min)" />
                          </div>
                        ) : (
                          <div className="form-row-3" style={{ gap: 4, marginTop: 4 }}>
                            <input className="form-input form-input-sm" type="number" min="1" value={fe.sets} onChange={e => updateExField(idx, 'sets', parseInt(e.target.value, 10) || 1)} placeholder="Sets" />
                            <input className="form-input form-input-sm" type="text" value={fe.reps} onChange={e => updateExField(idx, 'reps', e.target.value)} placeholder="Reps" />
                            <input className="form-input form-input-sm" type="number" min="0" value={fe.rest_seconds} onChange={e => updateExField(idx, 'rest_seconds', parseInt(e.target.value, 10) || 0)} placeholder="Desc(s)" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <button type="button" className="btn btn-secondary btn-full mb-12" onClick={openExPicker}>
                + Añadir ejercicio del admin
              </button>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowEditor(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving || !formName.trim()}>
                  {saving ? <span className="spinner spinner-sm" /> : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showExPicker && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowExPicker(false)}>
          <div className="modal-sheet">
            <div className="modal-handle" />
            <div className="modal-title">Añadir ejercicio del admin</div>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <span className="search-icon" style={{ left: 12 }}>🔍</span>
              <input
                className="search-input"
                style={{ paddingLeft: 36 }}
                placeholder="Buscar..."
                value={templateExSearch}
                onChange={e => handleExPickerSearch(e.target.value)}
              />
            </div>
            <div className="picker-list">
              {exPickerLoading ? (
                <div className="loading-container" style={{ padding: '20px 0' }}><div className="spinner" /></div>
              ) : availablePicker.length === 0 && !exPickerLoading ? (
                <div className="text-muted text-sm" style={{ textAlign: 'center', padding: '20px 0' }}>
                  No hay ejercicios del admin disponibles.
                </div>
              ) : (
                <>
                  {availablePicker.map(ex => (
                    <div key={ex.id} className="picker-item" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', flexWrap: 'wrap' }}
                        onClick={() => addExercise(ex)}
                      >
                        <span className={`badge badge-${(ex.category || 'other').toLowerCase()}`}>
                          {CATEGORY_LABELS[(ex.category || 'other').toLowerCase()] || ex.category || 'Otro'}
                        </span>
                        <span className="picker-item-name">{ex.name}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setInfoExercise(ex)}
                        style={{ flexShrink: 0, background: 'none', border: '1px solid #2a2a4a', borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0a0b0', cursor: 'pointer', fontSize: '0.8rem' }}
                        aria-label="Ver información"
                      >
                        i
                      </button>
                    </div>
                  ))}
                  <div ref={exPickerSentinelRef} style={{ height: 1 }} />
                  {exPickerLoadingMore && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                      <div className="spinner spinner-sm" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <ExerciseInfoModal exercise={infoExercise} onClose={() => setInfoExercise(null)} />
    </Layout>
  );
}
